import React from 'react'
import cn from 'classnames'
import { Check, ListMusic, Pause, Play, RadioTower } from 'lucide-react'
import audioStyles from 'components/answers/FileEmbed/audio.module.scss'
import { formatAudioTime } from 'components/answers/FileEmbed/AudioAttachmentPlayer'
import videoChromeStyles from 'components/shared/video-chrome.module.scss'
import { toggleTrackInPlaylist } from 'components/shared/MediaDock/mediaDockEvents'
import {
    playlistMenuLabel,
    useMediaKeyInPlaylist,
} from 'components/shared/MediaDock/useMediaKeyInPlaylist'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import styles from 'components/answers/radio-broadcast-transport.module.scss'

const swallow = event => {
    event.stopPropagation()
}

// The live card's transport is a tuner, not a player. It wears the audio
// player's own two-row scrim transport — the round 52px button, the seek line
// on top, the time and the playlist button below — so every music card reads
// the same, with a tuner where the play button sits. The station has
// one clock and one position for everyone; the seek line only shows it, it
// cannot be dragged, and pressing the button joins or leaves the broadcast
// through the dock. An aired audio play drops the tuner for the song's own
// inline player. Video songs use the non-live button in every card, since
// video only ever plays in the dock: `playing` shows pause while the dock
// plays it, and `replayLabel` names the action.
export default function RadioBroadcastTransport({
    isLive,
    tunedIn,
    position,
    duration,
    progress,
    onTuneIn = undefined,
    onTuneOut = undefined,
    onReplay = undefined,
    playing = false,
    replayLabel = 'Play from History',
    // The song's dock key and post, for the playlist button (bottom right).
    mediaKey = undefined,
    answerId = undefined,
    // Extra controls right of the playlist button (the Music header's upvalue).
    actions = null,
    // The song's total, left of the bottom row. The time moves up beside the
    // seek line to make room.
    leading = null,
}) {
    // Number(null) is 0: a missing position must not read as "0:00".
    const hasPosition = position != null && Number.isFinite(Number(position))
    const hasDuration = Number(duration) > 0
    const on = Boolean(isLive && tunedIn)
    const percent = Math.round((Number(progress) || 0) * 1000) / 10
    const press = handler => event => {
        event.stopPropagation()
        handler?.()
    }
    const inPlaylist = useMediaKeyInPlaylist(mediaKey)
    const cachedAnswer = useCachedAnswer(answerId ? String(answerId) : null)
    const playlistCount = Number(cachedAnswer?.playlist_count) || 0
    const time = (
        <span className={audioStyles.audioTimeText}>
            <span data-testid="radio-position">
                {hasPosition ? formatAudioTime(Number(position)) : '–:––'}
            </span>
            {' / '}
            <span data-testid="radio-duration">
                {hasDuration ? formatAudioTime(Number(duration)) : '–:––'}
            </span>
        </span>
    )

    return (
        <div
            className={cn(
                audioStyles.customAudioPlayer,
                audioStyles.customAudioPlayerScrimTheme,
                audioStyles.customAudioPlayerScrimLayout,
                leading && audioStyles.customAudioPlayerScrimWithLeading,
                styles.transport,
            )}
            data-testid="radio-broadcast-transport"
            data-tuned-in={on ? 'true' : 'false'}
            onMouseDown={swallow}
            onPointerDown={swallow}
            onTouchStart={swallow}
        >
            {isLive ? (
                <button
                    type="button"
                    className={cn(audioStyles.audioPlayButton, styles.tuner, on && styles.tunerOn)}
                    onClick={press(on ? onTuneOut : onTuneIn)}
                    aria-pressed={on}
                    aria-label={on ? 'Tune out' : 'Tune in'}
                    title={on ? 'Tune out' : 'Tune in'}
                    data-testid="radio-tune-toggle"
                    data-icon-only="true"
                >
                    <RadioTower className={audioStyles.audioPlayIcon} aria-hidden="true" />
                </button>
            ) : (
                <button
                    type="button"
                    className={cn(audioStyles.audioPlayButton, styles.tuner)}
                    onClick={press(onReplay)}
                    aria-label={replayLabel}
                    aria-pressed={playing || undefined}
                    title={replayLabel}
                    data-testid="radio-replay"
                    data-icon-only="true"
                >
                    {playing ? (
                        <Pause className={audioStyles.audioPlayIcon} aria-hidden="true" />
                    ) : (
                        <Play className={audioStyles.audioPlayIcon} aria-hidden="true" />
                    )}
                </button>
            )}
            {/* The player's seek line, read-only: the broadcast cannot be scrubbed. */}
            <div className={audioStyles.audioScrimSeekRow}>
                <input
                    type="range"
                    className={cn(
                        videoChromeStyles.seekRange,
                        audioStyles.audioScrimSeekRange,
                        styles.stationSeek,
                    )}
                    style={
                        /** @type {React.CSSProperties} */ ({
                            '--video-chrome-progress': `${percent}%`,
                        })
                    }
                    min="0"
                    max={hasDuration ? Number(duration) : 0}
                    step="0.01"
                    value={hasPosition ? Number(position) : 0}
                    readOnly
                    tabIndex={-1}
                    aria-label="Position in the song"
                    aria-readonly="true"
                    aria-valuetext={
                        hasPosition && hasDuration
                            ? `${formatAudioTime(Number(position))} of ${formatAudioTime(
                                  Number(duration),
                              )}`
                            : undefined
                    }
                    data-testid="radio-station-seek"
                />
                {leading && time}
            </div>
            <div className={audioStyles.audioScrimControlsRow}>
                {leading ? <div className={audioStyles.audioScrimLeading}>{leading}</div> : time}
                {(mediaKey || actions) && (
                    <div className={audioStyles.audioActions}>
                        {mediaKey && (
                            <button
                                type="button"
                                className={cn(
                                    audioStyles.audioPlaylistButton,
                                    inPlaylist && audioStyles.audioPlaylistButtonActive,
                                )}
                                data-testid="audio-player-playlist-button"
                                onClick={press(() =>
                                    toggleTrackInPlaylist(mediaKey, 'media_player'),
                                )}
                                aria-label={playlistMenuLabel(inPlaylist)}
                                aria-pressed={inPlaylist}
                                title={playlistMenuLabel(inPlaylist)}
                            >
                                {inPlaylist ? (
                                    <Check
                                        className={audioStyles.audioPlaylistButtonIcon}
                                        aria-hidden="true"
                                    />
                                ) : (
                                    <ListMusic
                                        className={audioStyles.audioPlaylistButtonIcon}
                                        aria-hidden="true"
                                    />
                                )}
                                {playlistCount > 0 && <span>{playlistCount}</span>}
                            </button>
                        )}
                        {actions}
                    </div>
                )}
            </div>
        </div>
    )
}
